<script setup lang="ts">
const api = useApi()
const toast = useToast()
const { fetchMe } = useAuth()
const route = useRoute()

const step = ref<'email' | 'code'>('email')
// Prefill from an admin-invite link (/login?email=...). route.query is identical
// on the server and client, so seeding the ref here is hydration-safe (no
// ClientOnly wrapper, which would re-mount and clear the field).
const qEmail = Array.isArray(route.query.email) ? route.query.email[0] : route.query.email
const email = ref((qEmail || '').toString())

// where to land after sign-in (?redirect= from the auth middleware / a public
// page's "sign in" link). Same-origin paths only — an absolute URL or
// protocol-relative //host would be an open redirect.
const redirect = computed(() => {
  const r = route.query.redirect
  const v = Array.isArray(r) ? r[0] : r
  return typeof v === 'string' && v.startsWith('/') && !v.startsWith('//') ? v : ''
})
const code = ref('')
const busy = ref(false)
const info = ref('')

const emailInput = ref<HTMLInputElement | null>(null)
const codeInput = ref<HTMLInputElement | null>(null)

// Normalize identically to the backend (.trim().toLowerCase()) so the OTP hash
// matches regardless of how the user typed their address.
function normEmail(v: string) { return (v || '').trim().toLowerCase() }

// Focus via ref after mount (not the autofocus attribute, which races
// hydration). We intentionally do NOT call /me here: there's no session yet on
// the login screen, so it would only produce a noisy 401.
onMounted(() => { emailInput.value?.focus() })

async function requestCode() {
  info.value = ''
  const e = normEmail(email.value)
  if (!e.includes('@')) { toast.error('enter a valid email'); return }
  busy.value = true
  try {
    await api.post('/auth/request', { email: e })
    step.value = 'code'
    info.value = `code sent to ${e}`
    await nextTick()
    codeInput.value?.focus()
  } catch (err: any) {
    toast.error(err?.data?.detail || 'could not send code')
  } finally { busy.value = false }
}

async function verify() {
  const c = code.value.trim()
  if (c.length !== 6) { toast.error('enter the 6-digit code'); return }
  busy.value = true
  try {
    await api.post('/auth/verify', { email: normEmail(email.value), code: c })
    const me = await fetchMe()
    // new players finish registration first; the destination rides along so
    // they still land where they were headed
    await navigateTo(me?.needs_registration
      ? (redirect.value ? `/register?redirect=${encodeURIComponent(redirect.value)}` : '/register')
      : (redirect.value || '/'))
  } catch (err: any) {
    toast.error(err?.data?.detail || 'verification failed')
  } finally { busy.value = false }
}

function backToEmail() {
  step.value = 'email'; code.value = ''; info.value = ''
  nextTick(() => emailInput.value?.focus())
}
</script>

<template>
  <div class="auth-shell">
    <div class="auth-card">
      <div class="brand">
        <img src="/BuzztimeLogo.webp" class="logo" alt="Buzztime" />
        <span class="dom">ProBook</span>
      </div>
      <p class="tag">play-money sportsbook · sign in with email</p>

      <template v-if="step === 'email'">
        <label class="fld-lbl">email</label>
        <input ref="emailInput" v-model="email" type="email" class="fld"
          autocomplete="email" placeholder="you@example.com" @keyup.enter="requestCode" />
        <button class="btn-primary" :disabled="busy" @click="requestCode">
          {{ busy ? 'sending…' : 'send code' }}
        </button>
      </template>

      <template v-else>
        <label class="fld-lbl">6-digit code</label>
        <input ref="codeInput" v-model="code" inputmode="numeric" maxlength="6"
          autocomplete="one-time-code" class="fld code-fld" placeholder="••••••"
          @keyup.enter="verify" />
        <button class="btn-primary" :disabled="busy" @click="verify">
          {{ busy ? 'verifying…' : 'verify & enter' }}
        </button>
        <button class="btn-link" @click="backToEmail">use a different email</button>
      </template>

      <div v-if="info" class="ok">{{ info }}</div>
      <p class="note">codes expire in 10 minutes · one-time use</p>
    </div>
  </div>
</template>

<style scoped>
.auth-shell { flex: 1; display: grid; place-items: center; padding: 24px; }
.auth-card { width: 100%; max-width: 380px; background: var(--panel); border: 1px solid var(--line); border-radius: 16px; padding: 32px; }
.brand { display: flex; align-items: center; gap: 12px; }
.brand .logo { height: 30px; width: auto; display: block; }
.brand .mark { font-weight: 900; font-size: 22px; color: var(--ink); letter-spacing: -0.04em; }
.brand .dom { font-family: var(--font-mono); font-size: 12px; color: var(--muted); text-transform: uppercase; letter-spacing: 0.14em; }
.tag { color: var(--muted); font-size: 13px; margin: 8px 0 24px; }
.fld-lbl { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); margin-bottom: 8px; }
.fld { width: 100%; background: var(--panel-2); border: 1px solid var(--line); color: var(--ink); font-family: var(--font-mono); font-size: 16px; padding: 12px 14px; border-radius: 10px; margin-bottom: 16px; }
.fld:focus { outline: none; border-color: var(--brand); }
.code-fld { letter-spacing: 0.5em; text-align: center; font-size: 22px; }
.btn-primary { width: 100%; color: #fff; padding: 13px; font-size: 15px; font-weight: 800; border-radius: 10px; text-align: center; background: linear-gradient(90deg, var(--brand) 0%, var(--brand-2) 100%); box-shadow: 0 6px 18px rgba(219, 86, 152, 0.28); }
.btn-primary:disabled { background: var(--line); color: var(--muted); cursor: not-allowed; }
.btn-link { width: 100%; background: none; color: var(--muted); font-size: 12px; margin-top: 12px; }
.btn-link:hover { color: var(--ink); }
.ok { color: var(--accent); font-size: 12px; margin-top: 14px; font-family: var(--font-mono); }
.note { color: var(--muted); font-size: 11px; margin-top: 20px; font-family: var(--font-mono); }
</style>
